import { useState, useRef, useEffect } from "react";
import AdminLayout from "../components/AdminLayout";

type PayTab = "alipay" | "wechat" | "qrcode";

const payTabs: { key: PayTab; label: string; icon: string }[] = [
  { key: "alipay", label: "支付宝配置", icon: "ri-alipay-line" },
  { key: "wechat", label: "微信支付配置", icon: "ri-wechat-pay-line" },
  { key: "qrcode", label: "收款码配置", icon: "ri-qr-code-line" },
];

// ==================== API 层 ====================

interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

const DEFAULT_CONFIG: PaymentConfig = {
  alipay_enabled: false,
  wechat_enabled: false,
  qrcode_enabled: false,
  qrcode_priority: false,
  alipay_qrcode: "",
  wechat_qrcode: "",
};

async function fetchPaymentConfig(): Promise<PaymentConfig> {
  const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
  const siteId = localStorage.getItem('current_site_id') || '';
  console.log("📡 正在请求支付配置...");
  const res = await fetch("/api/admin/settings/payment", {
    method: "GET",
    headers: {
      "Authorization": token ? `Bearer ${token}` : "",
      'X-Site-Id': siteId,
    },
  });
  const data = await res.json();
  console.log("📥 API响应:", data);
  
  if (data.code === 0 && data.data) {
    const config = {
      alipay_enabled: !!data.data.alipay_enabled,
      wechat_enabled: !!data.data.wechat_enabled,
      qrcode_enabled: !!data.data.qrcode_enabled,
      qrcode_priority: !!data.data.qrcode_priority,
      alipay_qrcode: data.data.alipay_qrcode || "",
      wechat_qrcode: data.data.wechat_qrcode || "",
    };
    console.log("✅ 解析后的配置:", config);
    return config;
  }
  console.warn("⚠️ API返回异常，使用默认配置");
  return { ...DEFAULT_CONFIG };
}

async function savePaymentConfig(config: PaymentConfig): Promise<{ code: number; message: string; data: PaymentConfig }> {
  const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
  const siteId = localStorage.getItem('current_site_id') || '';
  console.log("💾 开始保存配置:", config);
  
  const res = await fetch("/api/admin/settings/payment", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "Authorization": token ? `Bearer ${token}` : "",
      'X-Site-Id': siteId,
    },
    body: JSON.stringify(config),
  });
  
  const result = await res.json();
  console.log("💾 保存响应:", result);
  return result;
}

// ==================== 组件 ====================

function ToggleSwitch({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${value ? "bg-orange-500" : "bg-gray-200"}`}
    >
      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? "translate-x-4" : "translate-x-0"}`} />
    </button>
  );
}

function SaveBar({ onSave, saving, saved }: { onSave: () => void; saving: boolean; saved: boolean }) {
  return (
    <div className="flex items-center gap-3 pt-4 border-t border-gray-100 mt-6">
      <button
        onClick={onSave}
        disabled={saving}
        className="px-6 py-2.5 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? "保存中..." : "保存配置"}
      </button>
      {saved && (
        <span className="text-sm text-green-600 flex items-center gap-1.5">
          <i className="ri-checkbox-circle-line" />
          保存成功
        </span>
      )}
    </div>
  );
}

// ---- Alipay Tab ----
function AlipayTab({ config, onChange }: { config: PaymentConfig; onChange: (c: PaymentConfig) => void }) {
  const [form, setForm] = useState({
    appId: "",
    privateKey: "",
    publicKey: "",
    notifyUrl: "https://yourdomain.com/api/alipay/notify",
    returnUrl: "https://yourdomain.com/payment/success",
    sandbox: true,
  });

  const update = (k: string, v: string | boolean) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "sandbox") onChange({ ...config, alipay_enabled: v as boolean });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-blue-500 rounded-xl">
            <i className="ri-alipay-line text-white text-xl" />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800">支付宝支付</div>
            <div className="text-xs text-gray-500">通过支付宝开放平台收款</div>
          </div>
        </div>
        <ToggleSwitch value={config.alipay_enabled} onChange={(v) => onChange({ ...config, alipay_enabled: v })} />
      </div>

      <div className={`space-y-4 ${!config.alipay_enabled ? "opacity-50 pointer-events-none" : ""}`}>
        <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
          <i className="ri-information-line text-yellow-500" />
          <span className="text-xs text-yellow-700">请在支付宝开放平台创建应用后填写配置信息</span>
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">应用 ID（App ID）</label>
          <input value={form.appId} onChange={(e) => set("appId", e.target.value)} placeholder="如：2021001234567890" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 font-mono" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">应用私钥（RSA2）</label>
          <textarea value={form.privateKey} onChange={(e) => set("privateKey", e.target.value)} rows={4} placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none font-mono text-xs" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">支付宝公钥</label>
          <textarea value={form.publicKey} onChange={(e) => set("publicKey", e.target.value)} rows={4} placeholder="-----BEGIN PUBLIC KEY-----&#10;...&#10;-----END PUBLIC KEY-----" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none font-mono text-xs" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1.5">异步通知地址</label>
            <input value={form.notifyUrl} onChange={(e) => set("notifyUrl", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1.5">支付成功跳转地址</label>
            <input value={form.returnUrl} onChange={(e) => set("returnUrl", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
          </div>
        </div>
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <div className="text-sm font-medium text-gray-700">沙箱模式（测试环境）</div>
            <div className="text-xs text-gray-400 mt-0.5">开启后使用支付宝沙箱环境</div>
          </div>
          <ToggleSwitch value={form.sandbox} onChange={(v) => set("sandbox", v)} />
        </div>
      </div>
    </div>
  );

  function set(k: string, v: string) { setForm((f) => ({ ...f, [k]: v })); }
}

// ---- WeChat Tab ----
function WechatTab({ config, onChange }: { config: PaymentConfig; onChange: (c: PaymentConfig) => void }) {
  const [form, setForm] = useState({
    appId: "",
    mchId: "",
    apiKey: "",
    notifyUrl: "https://yourdomain.com/api/wechat/notify",
    sandbox: true,
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-green-500 rounded-xl">
            <i className="ri-wechat-pay-line text-white text-xl" />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800">微信支付</div>
            <div className="text-xs text-gray-500">通过微信商户平台收款</div>
          </div>
        </div>
        <ToggleSwitch value={config.wechat_enabled} onChange={(v) => onChange({ ...config, wechat_enabled: v })} />
      </div>

      <div className={`space-y-4 ${!config.wechat_enabled ? "opacity-50 pointer-events-none" : ""}`}>
        <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
          <i className="ri-information-line text-yellow-500" />
          <span className="text-xs text-yellow-700">请在微信商户平台申请开通微信支付后填写配置信息</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1.5">公众号/小程序 AppID</label>
            <input value={form.appId} onChange={(e) => set("appId", e.target.value)} placeholder="wx开头的AppID" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 font-mono" />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1.5">商户号（Mch ID）</label>
            <input value={form.mchId} onChange={(e) => set("mchId", e.target.value)} placeholder="10位数字商户号" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 font-mono" />
          </div>
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">API 密钥</label>
          <input value={form.apiKey} onChange={(e) => set("apiKey", e.target.value)} type="password" placeholder="32位API密钥" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 font-mono" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">异步通知地址</label>
          <input value={form.notifyUrl} onChange={(e) => set("notifyUrl", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400" />
        </div>
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <div className="text-sm font-medium text-gray-700">沙箱模式（测试环境）</div>
            <div className="text-xs text-gray-400 mt-0.5">开启后使用微信支付沙箱环境</div>
          </div>
          <ToggleSwitch value={form.sandbox} onChange={(v) => set("sandbox", v)} />
        </div>
      </div>
    </div>
  );

  function set(k: string, v: string) { setForm((f) => ({ ...f, [k]: v })); }
}

// ---- QRCode Tab ----
function QRCodeTab({ config, onChange }: { config: PaymentConfig; onChange: (c: PaymentConfig) => void }) {
  const alipayRef = useRef<HTMLInputElement>(null);
  const wechatRef = useRef<HTMLInputElement>(null);

  const QRUploader = ({
    label, value, onChange, inputRef, color
  }: { label: string; value: string; onChange: (v: string) => void; inputRef: React.RefObject<HTMLInputElement | null>; color: string }) => (
    <div className="flex-1">
      <label className="block text-sm font-medium text-gray-700 mb-3">{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        className={`w-40 h-40 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 transition-colors overflow-hidden ${value ? "border-gray-200" : "border-gray-200"}`}
      >
        {value ? (
          <img src={value} alt="qr" className="w-full h-full object-cover" />
        ) : (
          <>
            <i className={`ri-qr-code-line text-3xl ${color} mb-2`} />
            <span className="text-xs text-gray-400">点击上传收款码</span>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const base64 = e.target?.result as string;
              onChange(base64);
            };
            reader.readAsDataURL(f);
          }
        }}
      />
      {value && (
        <button onClick={() => onChange("")} className="mt-1 text-xs text-red-400 hover:text-red-500 cursor-pointer">
          清除图片
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* 收款码启用开关 */}
      <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-purple-500 rounded-xl">
            <i className="ri-qr-code-line text-white text-xl" />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800">收款码支付</div>
            <div className="text-xs text-gray-500">使用支付宝/微信收款码收取VIP费用</div>
          </div>
        </div>
        <ToggleSwitch value={config.qrcode_enabled} onChange={(v) => onChange({ ...config, qrcode_enabled: v })} />
      </div>

      {/* 优先使用收款码提示 */}
      {config.qrcode_enabled && (
        <div className="p-3 bg-amber-50 rounded-xl flex items-start gap-2">
          <i className="ri-information-line text-amber-500 mt-0.5" />
          <div className="text-xs text-amber-700">
            <p className="font-medium mb-1">优先使用收款码支付</p>
            <p className="text-amber-600">开启后，前台VIP开通页面将优先显示收款码支付方式</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-amber-600">收款码优先：</span>
              <ToggleSwitch value={config.qrcode_priority} onChange={(v) => onChange({ ...config, qrcode_priority: v })} />
            </div>
          </div>
        </div>
      )}

      <div className={`space-y-6 ${!config.qrcode_enabled ? "opacity-50 pointer-events-none" : ""}`}>
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-1">收款二维码</h3>
          <p className="text-xs text-gray-400 mb-4">上传支付宝和微信的收款二维码，用于展示给用户扫码付款</p>
          <div className="flex gap-8">
            <QRUploader label="支付宝收款码" value={config.alipay_qrcode} onChange={(v) => onChange({ ...config, alipay_qrcode: v, qrcode_enabled: v ? true : config.qrcode_enabled })} inputRef={alipayRef} color="text-blue-400" />
            <QRUploader label="微信收款码" value={config.wechat_qrcode} onChange={(v) => onChange({ ...config, wechat_qrcode: v, qrcode_enabled: v ? true : config.qrcode_enabled })} inputRef={wechatRef} color="text-green-400" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- 主页面 ----
export default function AdminPaymentPage() {
  const [activeTab, setActiveTab] = useState<PayTab>("qrcode");
  const [config, setConfig] = useState<PaymentConfig>({ ...DEFAULT_CONFIG });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadCount, setLoadCount] = useState(0);

  // 初始化加载配置
  useEffect(() => {
    const loadConfig = async () => {
      setLoadCount((c) => c + 1);
      console.log(`🔄 加载配置 (第${loadCount + 1}次)`);
      try {
        const data = await fetchPaymentConfig();
        setConfig(data);
        setLoading(false);
      } catch (err) {
        console.error("❌ 加载支付配置失败:", err);
        setLoading(false);
      }
    };
    loadConfig();
  }, []);

  const handleSave = async () => {
    console.log("🚀 handleSave 被调用，当前配置:", config);
    setSaving(true);
    try {
      const result = await savePaymentConfig(config);
      if (result.code === 0) {
        console.log("✅ 保存成功，后端返回:", result.data);
        setConfig(result.data);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        alert("保存失败: " + (result.message || "未知错误"));
      }
    } catch (err) {
      console.error("❌ 保存失败:", err);
      alert("保存失败，请检查网络");
    }
    setSaving(false);
  };

  const updateConfig = (newConfig: PaymentConfig) => {
    console.log("📝 配置更新:", { from: config, to: newConfig });
    setConfig(newConfig);
  };

  const renderTab = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }
    switch (activeTab) {
      case "alipay": return <AlipayTab config={config} onChange={updateConfig} />;
      case "wechat": return <WechatTab config={config} onChange={updateConfig} />;
      case "qrcode": return <QRCodeTab config={config} onChange={updateConfig} />;
      default: return null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div>
          <h1 className="text-xl font-bold text-gray-800">支付配置</h1>
          <p className="text-sm text-gray-500 mt-1">配置支付宝、微信支付及收款码，用于VIP会员收费</p>
        </div>

        <div className="flex gap-6">
          {/* Left Tab Nav */}
          <div className="w-44 shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {payTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2.5 w-full px-4 py-3.5 text-sm transition-colors cursor-pointer border-b border-gray-50 last:border-0 ${
                    activeTab === tab.key
                      ? "bg-orange-50 text-orange-600 font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${tab.icon} text-sm`} />
                  </div>
                  <span className="whitespace-nowrap">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Right Content */}
          <div className="flex-1 bg-white rounded-xl border border-gray-100 p-6 min-w-0">
            {renderTab()}
            <SaveBar onSave={handleSave} saving={saving} saved={saved} />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
